import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Plus,
  Loader2,
  KeyRound,
  Pencil,
  Power,
  Copy,
  CheckCircle2,
  ShieldAlert,
} from 'lucide-react';
import { apiFetch, apiFetchPaginated, ApiException } from '../../lib/api';
import { useAuthStore } from '../../lib/auth-store';
import { notify } from '../../lib/notify';
import {
  createUserSchema,
  editUserSchema,
  type CreateUserInput,
  type EditUserInput,
} from '../../lib/schemas';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';

interface AdminUser {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'supervisor' | 'agent' | 'customer';
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

const ROLE_LABEL: Record<AdminUser['role'], string> = {
  admin: 'Administrador',
  supervisor: 'Supervisor',
  agent: 'Agente',
  customer: 'Cliente',
};

const ROLE_STYLE: Record<AdminUser['role'], string> = {
  admin: 'text-purple-700 bg-purple-50',
  supervisor: 'text-indigo-700 bg-indigo-50',
  agent: 'text-blue-700 bg-blue-50',
  customer: 'text-slate-600 bg-slate-100',
};

export default function Users() {
  const role = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);
  const isAdmin = role === 'admin';

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [tempPasswordModal, setTempPasswordModal] = useState<{
    email: string;
    password: string;
  } | null>(null);

  // Estado de confirmaciones en curso
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null);
  const [toggleTarget, setToggleTarget] = useState<AdminUser | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    apiFetchPaginated<AdminUser>('/users?per_page=100')
      .then((res) => setUsers(res.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [reloadTick]);

  const refresh = () => setReloadTick((t) => t + 1);

  const doToggleActive = async () => {
    if (!toggleTarget) return;
    try {
      await apiFetch(`/users/${toggleTarget.id}`, {
        method: 'PATCH',
        body: { is_active: !toggleTarget.is_active },
      });
      notify.success(
        toggleTarget.is_active ? 'Usuario desactivado.' : 'Usuario reactivado.',
        toggleTarget.email,
      );
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar el estado.');
      throw err;
    }
  };

  const doResetPassword = async () => {
    if (!resetTarget) return;
    try {
      const result = await apiFetch<{ temporary_password: string }>(
        `/users/${resetTarget.id}/reset-password`,
        { method: 'POST' },
      );
      setTempPasswordModal({ email: resetTarget.email, password: result.temporary_password });
    } catch (err) {
      notify.apiError(err, 'No se pudo resetear la contraseña.');
      throw err;
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Equipo</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {isAdmin
              ? 'Crea, edita y desactivá las cuentas que pueden acceder al panel.'
              : 'Listado de usuarios con acceso al panel. Solo administradores pueden modificarlo.'}
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
          >
            <Plus size={14} />
            Nuevo usuario
          </button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        {loading && (
          <div className="px-5 py-12 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Cargando usuarios…
          </div>
        )}
        {error && !loading && (
          <div className="px-5 py-10 text-sm text-rose-600 text-center">{error}</div>
        )}
        {!loading && !error && users.length === 0 && (
          <div className="px-5 py-12 text-sm text-slate-500 text-center">No hay usuarios.</div>
        )}
        {!loading && !error && users.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Usuario</th>
                  <th className="px-5 py-3 font-medium">Rol</th>
                  <th className="px-5 py-3 font-medium">Estado</th>
                  <th className="px-5 py-3 font-medium">Última sesión</th>
                  {isAdmin && <th className="px-5 py-3 font-medium text-right">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50 transition">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{u.name}</div>
                      <div className="text-[11px] text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ROLE_STYLE[u.role]}`}
                      >
                        {ROLE_LABEL[u.role]}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {u.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                          Desactivado
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {u.last_login_at ? formatDate(u.last_login_at) : '—'}
                    </td>
                    {isAdmin && (
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingUser(u)}
                            title="Editar"
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setResetTarget(u)}
                            title="Generar nueva contraseña"
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <KeyRound size={14} />
                          </button>
                          <button
                            onClick={() => {
                              if (u.id === myId) {
                                notify.error('No puedes desactivar tu propio usuario.');
                                return;
                              }
                              setToggleTarget(u);
                            }}
                            title={u.is_active ? 'Desactivar' : 'Reactivar'}
                            disabled={u.id === myId}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <Power size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {isAdmin && (
        <CreateUserModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(email, password) => {
            setCreateOpen(false);
            if (password) setTempPasswordModal({ email, password });
            else notify.success('Usuario creado.', email);
            refresh();
          }}
        />
      )}

      {isAdmin && editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => {
            const email = editingUser.email;
            setEditingUser(null);
            notify.success('Usuario actualizado.', email);
            refresh();
          }}
        />
      )}

      <TempPasswordModal data={tempPasswordModal} onClose={() => setTempPasswordModal(null)} />

      <ConfirmDialog
        open={resetTarget !== null}
        onOpenChange={(o) => !o && setResetTarget(null)}
        title="Generar nueva contraseña"
        description={
          resetTarget && (
            <>
              Se generará una nueva contraseña temporal para{' '}
              <strong className="text-slate-900">{resetTarget.email}</strong>. La anterior dejará de
              funcionar inmediatamente.
            </>
          )
        }
        confirmLabel="Generar"
        tone="warning"
        onConfirm={doResetPassword}
      />

      <ConfirmDialog
        open={toggleTarget !== null}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={toggleTarget?.is_active ? 'Desactivar usuario' : 'Reactivar usuario'}
        description={
          toggleTarget && (
            <>
              {toggleTarget.is_active
                ? 'No podrá iniciar sesión hasta que vuelvas a activarlo. Los tickets ya asignados se mantienen.'
                : 'El usuario volverá a poder ingresar con sus credenciales actuales.'}
              <br />
              <strong className="text-slate-900">{toggleTarget.email}</strong>
            </>
          )
        }
        confirmLabel={toggleTarget?.is_active ? 'Desactivar' : 'Reactivar'}
        tone={toggleTarget?.is_active ? 'danger' : 'default'}
        onConfirm={doToggleActive}
      />
    </div>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-VE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------- CreateUserModal ----------

function CreateUserModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (email: string, temporaryPassword?: string) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserInput>({
    resolver: zodResolver(createUserSchema),
    defaultValues: { email: '', name: '', role: 'agent' },
  });

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const onSubmit = async (values: CreateUserInput) => {
    try {
      // Llamamos directo a fetch para acceder a meta.temporary_password
      // (apiFetch unwrappea sólo .data y descarta meta).
      const res = await fetch('/api/v1/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${useAuthStore.getState().accessToken ?? ''}`,
        },
        credentials: 'include',
        body: JSON.stringify(values),
      });
      const body = await res.json();
      if (!res.ok) throw new ApiException(res.status, body);
      reset();
      onCreated(values.email.trim(), body.meta?.temporary_password);
    } catch (err) {
      notify.apiError(err, 'No se pudo crear el usuario.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Crear nuevo usuario"
      description="El sistema generará una contraseña temporal que verás una sola vez."
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <Field
          label="Correo electrónico"
          error={errors.email?.message}
          input={
            <input
              type="email"
              autoComplete="off"
              placeholder="agente@ejemplo.com"
              {...register('email')}
              className={inputClass(!!errors.email)}
            />
          }
        />
        <Field
          label="Nombre completo"
          error={errors.name?.message}
          input={
            <input
              type="text"
              autoComplete="off"
              {...register('name')}
              className={inputClass(!!errors.name)}
            />
          }
        />
        <Field
          label="Rol"
          hint="Los administradores gestionan usuarios y configuración. Los supervisores ven el equipo pero no lo editan."
          error={errors.role?.message}
          input={
            <select {...register('role')} className={inputClass(!!errors.role)}>
              <option value="agent">Agente</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Administrador</option>
            </select>
          }
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            Crear usuario
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- EditUserModal ----------

function EditUserModal({
  user,
  onClose,
  onSaved,
}: {
  user: AdminUser;
  onClose: () => void;
  onSaved: () => void;
}) {
  const myId = useAuthStore((s) => s.user?.id);
  const isSelf = user.id === myId;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditUserInput>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      name: user.name,
      role: user.role === 'customer' ? 'agent' : user.role,
    },
  });

  const onSubmit = async (values: EditUserInput) => {
    try {
      // Si está editando su propio usuario, no enviamos rol para que el backend
      // tampoco lo permita por accidente.
      const payload = isSelf ? { name: values.name } : values;
      await apiFetch(`/users/${user.id}`, {
        method: 'PATCH',
        body: payload,
      });
      onSaved();
    } catch (err) {
      notify.apiError(err, 'No se pudo guardar.');
    }
  };

  return (
    <Modal open={true} onClose={onClose} title="Editar usuario" description={user.email}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <Field
          label="Nombre"
          error={errors.name?.message}
          input={
            <input
              type="text"
              {...register('name')}
              className={inputClass(!!errors.name)}
            />
          }
        />
        <Field
          label="Rol"
          error={errors.role?.message}
          input={
            <select
              {...register('role')}
              disabled={isSelf}
              className={`${inputClass(!!errors.role)} disabled:bg-slate-50 disabled:text-slate-500`}
            >
              <option value="agent">Agente</option>
              <option value="supervisor">Supervisor</option>
              <option value="admin">Administrador</option>
            </select>
          }
          hint={isSelf ? 'No puedes cambiar tu propio rol.' : undefined}
        />

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {isSubmitting && <Loader2 size={14} className="animate-spin" />}
            Guardar cambios
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- TempPasswordModal ----------

function TempPasswordModal({
  data,
  onClose,
}: {
  data: { email: string; password: string } | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.password);
      setCopied(true);
      notify.success('Contraseña copiada al portapapeles.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notify.error('No se pudo copiar.', 'Seleccionala y copiala manualmente.');
    }
  };

  return (
    <Modal
      open={data !== null}
      onClose={onClose}
      title="Contraseña temporal generada"
      description="Compartila por un canal seguro. Se muestra una sola vez."
    >
      {data && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              No se guardará en ningún log. Si cerrás esta ventana sin copiarla, vas a tener que
              generar otra desde el listado.
            </span>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Usuario</label>
            <div className="px-3 py-2 text-sm rounded-lg bg-slate-50 text-slate-700 border border-slate-200">
              {data.email}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Contraseña temporal
            </label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={data.password}
                readOnly
                className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-slate-200 bg-white"
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                onClick={handleCopy}
                className="px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center gap-1.5"
              >
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiada' : 'Copiar'}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Pedile al usuario que la cambie en su primera sesión desde Configuración → Mi perfil.
            </p>
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
            >
              Listo
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------- helpers ----------

function inputClass(hasError: boolean) {
  return `w-full px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2 ${
    hasError ? 'border-rose-300 focus:ring-rose-500' : 'border-slate-200 focus:ring-blue-500'
  }`;
}

function Field({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {input}
      {error ? (
        <p className="text-xs text-rose-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}
